import React, { useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import VoilaBoardService from "../../../_services/VoilaBoardService";

interface Props {
  show: boolean;
  onHide: () => void;
  boardId: number;
  onItemAdded: () => void;
}

const ITEM_TYPES = [
  { key: "photo", label: "Photo", icon: "\u{1F4F7}" },
  { key: "video", label: "Video", icon: "\u{1F3AC}" },
  { key: "youtube", label: "YouTube", icon: "\u{25B6}\u{FE0F}" },
  { key: "pdf", label: "PDF", icon: "\u{1F4C4}" },
  { key: "article", label: "Article", icon: "\u{1F4DD}" },
  { key: "text", label: "Text", icon: "\u{1F4AC}" },
];

const AddItemModal: React.FC<Props> = ({ show, onHide, boardId, onItemAdded }) => {
  const [activeType, setActiveType] = useState("photo");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [youTubeUrl, setYouTubeUrl] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setEventDate("");
    setYouTubeUrl("");
    setFile(null);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let mediaUrl: string | undefined;

      if (file) {
        const uploadRes = await VoilaBoardService.uploadMedia(boardId, file);
        if (uploadRes.data?.data?.url) {
          mediaUrl = uploadRes.data.data.url;
        }
      }

      await VoilaBoardService.addItem(boardId, {
        itemType: activeType,
        title: title || undefined,
        description: description || undefined,
        eventDate: eventDate || undefined,
        mediaUrl,
        youTubeUrl: activeType === "youtube" ? youTubeUrl : undefined,
      });

      resetForm();
      onItemAdded();
      onHide();
    } catch (err) {
      console.error("Failed to add item:", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Add Item to Board</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Type selector — inline styles to avoid global CSS overrides */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
          {ITEM_TYPES.map((t) => (
            <div
              key={t.key}
              onClick={() => setActiveType(t.key)}
              style={{
                padding: "6px 14px",
                borderRadius: 20,
                cursor: "pointer",
                fontSize: 14,
                border: activeType === t.key ? "2px solid #667eea" : "1px solid #ccc",
                background: activeType === t.key ? "#667eea" : "#fff",
                color: activeType === t.key ? "#fff" : "#333",
                fontWeight: activeType === t.key ? 600 : 400,
              }}
            >
              {t.icon} {t.label}
            </div>
          ))}
        </div>

        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Title / Caption</Form.Label>
            <Form.Control value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Sports Day 2025" />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description / Story</Form.Label>
            <Form.Control as="textarea" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Tell the story behind this moment..." />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Event Date</Form.Label>
            <Form.Control type="date" value={eventDate} onChange={(e) => setEventDate(e.target.value)} />
          </Form.Group>

          {activeType === "youtube" ? (
            <Form.Group className="mb-3">
              <Form.Label>YouTube URL</Form.Label>
              <Form.Control value={youTubeUrl} onChange={(e) => setYouTubeUrl(e.target.value)} placeholder="https://youtube.com/watch?v=..." />
            </Form.Group>
          ) : activeType !== "text" ? (
            <Form.Group className="mb-3">
              <Form.Label>Upload File</Form.Label>
              <Form.Control type="file" onChange={(e: any) => setFile(e.target.files?.[0] || null)} />
            </Form.Group>
          ) : null}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Cancel</Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? "Adding..." : "Add Item"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddItemModal;
